import React, { useCallback, useMemo, useState } from "react"
import { Button, Popover, Switch, Tooltip } from "antd"
import { Icon } from "components"
import Strings from "utils/strings"

// Every column the user can show/hide and reorder, in the order the table uses
// until the user drags them into their own order.
//
// Not listed here because the always-present `selection` column already renders
// them: serviceType, warnings and errors.
// `boxesOrBags` covers both the boxes and the bag id, so there is no separate
// bagId entry either.
export const ORDER_COLUMNS = [
	"orderId",
	"lot",
	"status",
	"payment",
	"deliveryDate",
	"slot",
	"senderName",
	"clientName",
	"phoneNumber",
	"orderAddress",
	"formattedAddress",
	"latitude",
	"longitude",
	"boxesOrBags",
	"deliveredDate",
	"arrivalTime",
	"departureTime",
] as const

export type OrderColumnKey = (typeof ORDER_COLUMNS)[number]

// What the table showed before the picker existed — also what "reset" restores.
const DEFAULT_VISIBLE_COLUMNS: OrderColumnKey[] = [
	"orderId",
	"lot",
	"status",
	"payment",
	"deliveryDate",
	"slot",
	"senderName",
	"clientName",
	"phoneNumber",
	"formattedAddress",
	"boxesOrBags",
]

export type OrderColumnsPreference = {
	// Every known column, in the user's chosen order
	order: OrderColumnKey[]
	// The subset of `order` that is actually rendered
	visible: OrderColumnKey[]
}

export const DEFAULT_ORDER_COLUMNS: OrderColumnsPreference = {
	order: [...ORDER_COLUMNS],
	visible: DEFAULT_VISIBLE_COLUMNS,
}

const STORAGE_KEY = "dingoo.orders.columns"

const isOrderColumnKey = (value: any): value is OrderColumnKey =>
	(ORDER_COLUMNS as readonly string[]).includes(value)

const toColumnKeys = (value: any): OrderColumnKey[] =>
	Array.isArray(value) ? value.filter(isOrderColumnKey) : []

const readStoredPreference = (): OrderColumnsPreference => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return DEFAULT_ORDER_COLUMNS

		const parsed = JSON.parse(raw)

		// Keys that no longer exist are dropped, and columns added since the
		// preference was saved are appended (hidden) so the saved layout survives.
		const storedOrder = toColumnKeys(parsed?.order)
		const order = [
			...storedOrder,
			...ORDER_COLUMNS.filter((key) => !storedOrder.includes(key)),
		]

		const visible = toColumnKeys(parsed?.visible)

		return {
			order,
			visible: visible.length > 0 ? visible : DEFAULT_VISIBLE_COLUMNS,
		}
	} catch {
		return DEFAULT_ORDER_COLUMNS
	}
}

/**
 * Order and visibility of the orders table columns, persisted per browser in
 * localStorage. Returns the current preference plus a setter that normalizes
 * and stores it.
 */
export const useOrderColumnsPreference = (): [
	OrderColumnsPreference,
	(preference: OrderColumnsPreference) => void,
] => {
	const [preference, setPreference] =
		useState<OrderColumnsPreference>(readStoredPreference)

	const updatePreference = useCallback(
		({ order, visible }: OrderColumnsPreference) => {
			// Keep at least one column on screen
			if (visible.length === 0) return

			const next: OrderColumnsPreference = {
				order,
				// Store visibility in the same order as the columns themselves
				visible: order.filter((key) => visible.includes(key)),
			}

			setPreference(next)

			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
			} catch {
				// Storage unavailable (private mode / quota) — keep the in-memory choice
			}
		},
		[]
	)

	return [preference, updatePreference]
}

/** The columns the table should render, in the user's order. */
export const getVisibleColumnKeys = ({
	order,
	visible,
}: OrderColumnsPreference): OrderColumnKey[] =>
	order.filter((key) => visible.includes(key))

const isDefaultPreference = ({ order, visible }: OrderColumnsPreference) =>
	order.every((key, index) => key === ORDER_COLUMNS[index]) &&
	visible.length === DEFAULT_VISIBLE_COLUMNS.length &&
	DEFAULT_VISIBLE_COLUMNS.every((key) => visible.includes(key))

// Labels are read lazily so they follow the active language.
const getColumnLabels = (): Record<OrderColumnKey, string | undefined> => ({
	orderId: Strings.orders.orderId,
	lot: Strings.orders.lot,
	status: Strings.orders.status,
	payment: Strings.orders.payment,
	deliveryDate: Strings.orders.deliveryDate,
	slot: Strings.orders.deliverySlot,
	senderName: Strings.orders.senderName,
	clientName: Strings.orders.clientName,
	phoneNumber: Strings.orders.phoneNumber,
	orderAddress: Strings.orders.orderAddress,
	formattedAddress: Strings.orders.formattedOrderAddress,
	latitude: Strings.orders.latitude,
	longitude: Strings.orders.longitude,
	boxesOrBags: Strings.orders.boxes,
	deliveredDate: Strings.orders.deliveredDate,
	arrivalTime: Strings.orders.arrivalTime,
	departureTime: Strings.orders.departureTime,
})

const moveColumn = (
	order: OrderColumnKey[],
	key: OrderColumnKey,
	targetKey: OrderColumnKey
) => {
	const next = order.filter((current) => current !== key)
	next.splice(order.indexOf(targetKey), 0, key)
	return next
}

interface ColumnSelectionDropdownProps {
	preference: OrderColumnsPreference
	setPreference: (preference: OrderColumnsPreference) => void
}

const ColumnSelectionDropdown: React.FC<ColumnSelectionDropdownProps> = ({
	preference,
	setPreference,
}) => {
	const labels = useMemo(getColumnLabels, [])

	// The row whose handle is being held — HTML5 drag only starts from the handle
	const [handleKey, setHandleKey] = useState<OrderColumnKey | null>(null)
	// Reordering stays local while dragging so the table below is not rebuilt on
	// every hover; it is committed on drop.
	const [draftOrder, setDraftOrder] = useState<OrderColumnKey[] | null>(null)

	const order = draftOrder || preference.order
	const { visible } = preference

	const hiddenCount = ORDER_COLUMNS.length - visible.length
	const isCustomized = !isDefaultPreference(preference)

	const toggleColumn = useCallback(
		(key: OrderColumnKey, checked: boolean) => {
			setPreference({
				order: preference.order,
				visible: checked
					? [...preference.visible, key]
					: preference.visible.filter((current) => current !== key),
			})
		},
		[preference, setPreference]
	)

	const handleDragOver = useCallback(
		(targetKey: OrderColumnKey) => {
			if (!handleKey || handleKey === targetKey) return
			setDraftOrder((current) =>
				moveColumn(current || preference.order, handleKey, targetKey)
			)
		},
		[handleKey, preference.order]
	)

	const commitDrag = useCallback(() => {
		if (draftOrder) setPreference({ order: draftOrder, visible })
		setDraftOrder(null)
		setHandleKey(null)
	}, [draftOrder, visible, setPreference])

	const content = (
		<div style={{ width: 320 }}>
			<div style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 8 }}>
				{Strings.orders.columnsDragHint}
			</div>

			<div style={{ maxHeight: 380, overflowY: "auto" }}>
				{order.map((key) => {
					const isDragging = Boolean(draftOrder) && handleKey === key
					return (
						<div
							key={key}
							draggable={handleKey === key}
							onDragStart={(event) => {
								// Firefox only starts a drag once data is set
								event.dataTransfer.setData("text/plain", key)
								event.dataTransfer.effectAllowed = "move"
							}}
							onDragOver={(event) => {
								event.preventDefault()
								handleDragOver(key)
							}}
							onDrop={(event) => {
								event.preventDefault()
								commitDrag()
							}}
							onDragEnd={commitDrag}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								padding: "6px 4px",
								borderRadius: 4,
								opacity: isDragging ? 0.4 : 1,
								backgroundColor: isDragging ? "#e6f7ff" : undefined,
							}}
						>
							<span
								onMouseDown={() => setHandleKey(key)}
								onMouseUp={() => !draftOrder && setHandleKey(null)}
								style={{ cursor: "grab", color: "#bfbfbf", display: "flex" }}
							>
								<Icon name="drag" />
							</span>
							<span
								style={{
									flex: 1,
									minWidth: 0,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									color: visible.includes(key) ? undefined : "#8c8c8c",
								}}
							>
								{labels[key]}
							</span>
							<Switch
								size="small"
								checked={visible.includes(key)}
								// The table always needs one column left standing
								disabled={visible.length === 1 && visible.includes(key)}
								onChange={(checked) => toggleColumn(key, checked)}
							/>
						</div>
					)
				})}
			</div>

			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 8,
					marginTop: 12,
					paddingTop: 12,
					borderTop: "1px solid #f0f0f0",
				}}
			>
				<span style={{ color: "#8c8c8c", fontSize: 12 }}>
					{Strings.formatString(
						Strings.orders.columnsVisibleCount || "{0}/{1}",
						visible.length,
						ORDER_COLUMNS.length
					)}
				</span>
				<div style={{ display: "flex", gap: 8 }}>
					<Button
						size="small"
						onClick={() =>
							setPreference({ order: preference.order, visible: [...order] })
						}
						disabled={hiddenCount === 0}
					>
						{Strings.orders.selectAllColumns}
					</Button>
					<Button
						size="small"
						onClick={() => setPreference(DEFAULT_ORDER_COLUMNS)}
						disabled={!isCustomized}
					>
						{Strings.orders.resetColumns}
					</Button>
				</div>
			</div>
		</div>
	)

	return (
		<Popover
			content={content}
			title={Strings.orders.selectColumns}
			trigger="click"
			placement="bottomRight"
		>
			<Tooltip title={Strings.orders.selectColumns}>
				<Button style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<Icon name="grid" style={{ fontSize: 14 }} />
				</Button>
			</Tooltip>
		</Popover>
	)
}

export default ColumnSelectionDropdown
